import React, { useState } from "react";
import { Button, Checkbox, Form, message, Modal, Spin, Switch } from "antd";
import { getPreShippingSetting, PreShippingSetting, savePreShippingSetting } from "@/services/api/preShippingSetting";
import { useRequest } from "ahooks";
import ShopDTO from "@/dto/shop/ShopDTO";
import { InjectBaseModalProps } from "@/hooks/Modal/ModalStore";
import { useModalStore } from "@/hooks/Modal/hooks";
import { getRequiredRule } from "@/utils/formValidatorRules";
import { InfoCircleOutlined } from "@ant-design/icons";

interface PreShipSettingProps {
    shop: ShopDTO;
}

interface AutoShipSettingTipProps extends InjectBaseModalProps {
    handleOk?(): void;

    handleCancel?(): void;
}

export const AutoShipSettingTipModal: React.FC<AutoShipSettingTipProps> = React.memo(function (props) {
    const { open, onCancel, handleOk, handleCancel } = props;

    const [form] = Form.useForm<FormValues>();

    return (
        <Modal
            width={700}
            open={open}
            onCancel={() => {
                onCancel();

                handleCancel?.();
            }}
            mask={{ closable: false }}
            footer={
                handleOk ? undefined : (
                    <Button type="primary" onClick={onCancel}>
                        知道了
                    </Button>
                )
            }
            title="免责声明"
            okText="同意"
            onOk={async () => {
                await form.validateFields();

                onCancel();

                handleOk?.();
            }}
        >
            <Form form={form} style={{ width: 500, margin: "0 auto" }}>
                <div className="mt5 text-gray">
                    <p className="mb5">
                        1.当您使用“自动发货”时，应知晓此为商家发货的辅助功能，会受到服务器稳定性，网络波动，合作方接口等因素影响，存在少数订单出现延迟发货或发货失败的风险
                    </p>
                    <p>
                        2.您知晓“自动发货”存在上述风险，无法完全替代手动发货及相关确认操作。请及时确认预发货订单的发货情况，发货易软件将不承担因未及时确认订单发货情况造成的损失。
                    </p>
                </div>
                {handleOk ? (
                    <Form.Item
                        name="enableScheduledShipping"
                        valuePropName="checked"
                        rules={[getRequiredRule("免责声明必须勾选")]}
                    >
                        <Checkbox>我已阅读并同意“免责声明”</Checkbox>
                    </Form.Item>
                ) : null}
            </Form>
        </Modal>
    );
});

interface FormValues
    extends Pick<PreShippingSetting, "enabled" | "enableAutoShippingByTracking" | "enableScheduledShipping"> {}

const PreShipSetting: React.FC<PreShipSettingProps> = function (props) {
    const { shop } = props;

    const [checked, setChecked] = useState(false);

    const modalStore = useModalStore();

    const { loading, refreshAsync } = useRequest(async () => {
        const values = await getPreShippingSetting(shop.id);

        setChecked(values.enabled);
    });

    const { runAsync: handleSave, loading: loading2 } = useRequest(
        async (enabled: boolean) => {
            await savePreShippingSetting({
                shopId: shop.id,
                enabled,
                enableScheduledShipping: enabled,
                enableAutoShippingByTracking: enabled,
            });

            message.success("保存设置成功");

            await refreshAsync();
        },
        {
            manual: true,
        },
    );

    return (
        <div style={{ border: "1px #dddddd solid", padding: "4px 8px" }} className="mb10">
            <Spin spinning={loading || loading2}>
                <div style={{ display: "flex", alignItems: "center" }} className="mb5">
                    <span className="mr5" style={{ fontWeight: 500 }}>
                        是否启用有揽件信息后自动发货
                    </span>

                    <Switch
                        checkedChildren="开"
                        unCheckedChildren="关"
                        checked={checked}
                        onChange={(checked) => {
                            if (checked) {
                                modalStore.openModal(AutoShipSettingTipModal, {
                                    handleOk() {
                                        handleSave(checked);
                                    },
                                    handleCancel() {
                                        setChecked(false);
                                    },
                                });
                            } else {
                                handleSave(checked);
                            }
                        }}
                    />

                    <span className="text-gray text-small ml5">
                        (因部分不可抗力情况，开启后无法保证100%成功，请定期确认订单发货情况。
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();

                                modalStore.openModal(AutoShipSettingTipModal);
                            }}
                        >
                            查看免责声明
                        </a>
                        )
                    </span>
                </div>
            </Spin>

            <p style={{ color: "#8a6d3b" }}>
                <InfoCircleOutlined className="mr10" />
                自动发货目前仅支持圆通、中通、申通、韵达、顺丰、极兔、德邦、安能、京东快递、天天、跨越、众邮等快递打印的电子面单号，且不同平台支持的快递有差异，如果平台支持多包裹发货且订单有多运单号时会自动尝试使用多包裹发货！
            </p>
        </div>
    );
};

export default React.memo(PreShipSetting);
